import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export default function TaskSummaryPanel({
  taskTitle,
  summaryRows,
  activeQuickEdit,
  openQuickEdit,
  quickEditLoading,
  quickEditDraft,
  setQuickEditDraft,
  statusOptions,
  userOptions,
  leadOptions,
  serviceOptions,
  parentOptions,
  priorityOptions,
  closeQuickEdit,
  handleQuickEditSave,
  saving,
  priorityColor,
}) {
  const activeQuickEditRow = summaryRows.find((item) => item.key === activeQuickEdit) ?? null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ bgcolor: '#2f9ad1', color: '#fff', px: 2, py: 1.25, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {taskTitle}
        </Typography>
      </Box>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {summaryRows.map((item, idx) => (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.35,
                borderBottom:
                  idx === summaryRows.length - 1 && !activeQuickEdit
                    ? 'none'
                    : '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {item.label}:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {item.chip ? (
                    <Chip
                      size="small"
                      label={item.value}
                      color={priorityColor(item.value)}
                      variant="outlined"
                    />
                  ) : (
                    item.value
                  )}
                </Typography>
                {item.editable ? (
                  <IconButton
                    size="small"
                    onClick={() => openQuickEdit(item.key)}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
          ))}

          {activeQuickEdit ? (
            <Box sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Edit {activeQuickEditRow?.label ?? 'detail'}
              </Typography>

              {quickEditLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading options...
                  </Typography>
                </Box>
              ) : (
                <>
                  {activeQuickEdit === 'status' ? (
                    <Autocomplete
                      options={statusOptions}
                      value={quickEditDraft}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Status" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'priority' ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Priority"
                      value={quickEditDraft ?? ''}
                      onChange={(e) => setQuickEditDraft(e.target.value)}
                    >
                      {priorityOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.charAt(0).toUpperCase() + item.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null}

                  {activeQuickEdit === 'responsible' ? (
                    <Autocomplete
                      options={userOptions}
                      value={quickEditDraft}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => (
                        <TextField {...params} label="Responsible person" fullWidth size="small" />
                      )}
                    />
                  ) : null}

                  {activeQuickEdit === 'deadline' ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="Deadline"
                      value={quickEditDraft ?? ''}
                      onChange={(e) => setQuickEditDraft(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : null}

                  {activeQuickEdit === 'lead' ? (
                    <Autocomplete
                      options={leadOptions}
                      value={quickEditDraft}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Project / lead" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'service' ? (
                    <Autocomplete
                      options={serviceOptions}
                      value={quickEditDraft}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Service" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'parent' ? (
                    <Autocomplete
                      options={parentOptions}
                      value={quickEditDraft}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Parent task" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'participants' ? (
                    <Autocomplete
                      multiple
                      options={userOptions}
                      value={Array.isArray(quickEditDraft) ? quickEditDraft : []}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Participants" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'observers' ? (
                    <Autocomplete
                      multiple
                      options={userOptions}
                      value={Array.isArray(quickEditDraft) ? quickEditDraft : []}
                      onChange={(_, v) => setQuickEditDraft(v)}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => <TextField {...params} label="Observers" fullWidth size="small" />}
                    />
                  ) : null}

                  {activeQuickEdit === 'template' ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(quickEditDraft)}
                          onChange={(e) => setQuickEditDraft(e.target.checked)}
                        />
                      }
                      label="Save as template"
                    />
                  ) : null}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                    <Button size="small" onClick={closeQuickEdit}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleQuickEditSave}
                      disabled={saving || quickEditLoading}
                    >
                      {saving ? <CircularProgress size={16} color="inherit" /> : 'Update'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
